import type { SearchResultHit, LoadedItem } from '@mimir/common/dto/searchResultHit';
import type { TimedMetadataItem, TimedMetadataItemWithUsageRule } from '@mimir/common/dto/timedMetadataModels';
import type { UsageRuleDateRange } from '@mimir/common/dto/timedMdfUsage';
import type { MultiItemUsageRestrictionWarningDialogRequest } from '@mimir/web/dialogs/multiItemUsageRestrictionWarningDialogRequest';
import type { EventUsageRestrictionWarningDialogRequest, UsageRestrictionWarningDialogRequest } from '@mimir/web/dialogs/usageRestrictionWarningDialogRequest';
import { showMultiItemUsageRestrictionWarningDialog } from '@mimir/web/dialogs/multiItemUsageRestrictionWarning';
import type { TimeRange } from '@mimir/common/util/timeRange';
import { isLoadedItem } from '@mimir/common/search/interfaces';
import type { TimedMetadataItemModel } from '@mimir/web/model/timedMdf';
import type { CalculatedUsageContext, UsageRuleWithCalculatedValidity } from '@mimir/common/util/timedMetadata';
import { doesSearchHitHaveRestrictedEvents, getUsageRuleInfoFromTimedMdf } from '@mimir/common/util/timedMetadata';
import { htmlEscape } from '@mimir/common/util/escape';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import { RESTRICTED_MATERIAL_MDF_ID } from '@mimir/common/mdf/mdfDefaults';
import { RestrictedMaterialField } from '@mimir/common/mdf/MetaDataForm';
import type { Segment} from '@mimir/common/util/intervals';
import { segmentOverlap } from '@mimir/common/util/intervals';
import { showUsageRestrictionWarningDialog } from '@mimir/web/dialogs/usageRestrictionWarning';
import { showEventUsageRestrictionWarningDialog } from '../dialogs/eventRestrictionWarning';
import type { ItemMediaPlayerOps } from '../model/itemMediaPlayerOps';

/**
 * A utility that components can use to handle showing
 * usage restriction warnings for an item.
 */
export function withUsageRestrictionCheck(
  item: SearchResultHit | LoadedItem,
  loadedTimedMdf: TimedMetadataItemModel[] | undefined,
  subclipRange: TimeRange | undefined,
  callback: () => any,
  playerOps: ItemMediaPlayerOps,
  setSelectedItem: ((item: TimedMetadataItem) => void) | null,
  getTimedMdfs: () => MetaDataSchema[],
) {
  // If we are in the search result view, then the hasRestrictedMaterial property or
  // indexed rules indicates restricted material. When we have a loaded item we look at the
  // currently loaded timed mdf store data.
  let hasRestrictions = false;
  if (item.hasRestrictedMaterial) {
    hasRestrictions = doesSearchHitHaveRestrictedEvents(item);
  } else if (isLoadedItem(item)) {
    hasRestrictions = hasRestrictedMaterial({items: loadedTimedMdf ?? []}, subclipRange);
  }
  if (!hasRestrictions) {
    callback();
  } else {
    const dialogReq: UsageRestrictionWarningDialogRequest = {
      item,
      timeRange: subclipRange,
      callback,
      playerOps,
      setSelectedItem,
      getTimedMdfs,
    };
    showUsageRestrictionWarningDialog(dialogReq);
  }
}

/**
 * A utility that components can use to handle showing
 * usage restriction warnings for a group of search result hits.
 */
export function withMultiItemUsageRestrictionCheck(
  items: SearchResultHit[],
  callback: () => any,
  playerOps: ItemMediaPlayerOps,
  getTimedMdfs: () => MetaDataSchema[],
) {
  if (items.length === 1) {
    // If there is only one item selected, then we
    // instead show the single-item warning dialog.
    withUsageRestrictionCheck(items[0], [], undefined, callback, playerOps, null, getTimedMdfs);
    return;
  }

  const itemsWithRestrictions = items.filter(it => it.hasRestrictedMaterial);
  if (itemsWithRestrictions.length === 0) {
    callback();
  } else {
    const dialogReq: MultiItemUsageRestrictionWarningDialogRequest = {
      items: itemsWithRestrictions,
      callback,
      getTimedMdfs,
    };
    showMultiItemUsageRestrictionWarningDialog(dialogReq);
  }
}

/**
 * A utility that components can use to handle showing
 * usage restriction warnings for timed events.
 */
export function eventUsageRestrictionCheck(
  item: SearchResultHit | LoadedItem,
  selectedEvents: TimedMetadataItem[],
  timedMdfs: TimedMetadataItemModel[] | undefined,
  callback: () => any,
  playerOps: ItemMediaPlayerOps,
  setSelectedItem: ((item: TimedMetadataItem) => void) | null,
  getTimedMdfs: () => MetaDataSchema[],
) {

  let hasRestrictions = false;
  if (isLoadedItem(item)) {
    for (const event of selectedEvents) {
      const range: TimeRange = {
        start: event.startMs,
        end: event.endMs,
      };

      hasRestrictions = hasRestrictedMaterial({ items: timedMdfs ?? [] }, range);

      // If any event has restrictions then we should show the dialog.
      if (hasRestrictions) {
        break;
      }
    }
  }

  if (!hasRestrictions) {
    callback();
  } else {
    const dialogReq: EventUsageRestrictionWarningDialogRequest = {
      item,
      selectedEvents: selectedEvents,
      callback,
      playerOps,
      setSelectedItem,
      getTimedMdfs,
    };
    showEventUsageRestrictionWarningDialog(dialogReq);
  }
}

/**
 * Definition of an object that components can use to display information
 * about usage rules.
 */
export interface UsageRuleInfo {
  /**
   * User visible name of the rule
   */
  label: string;
  /**
   * Summary defining whether rule is allowed/disallowed and comma separated
   * values of the disallowed contexts if any
   */
  summaryComment: string;
  hasDisallowedContext: boolean;
  color: 'green' | 'error';
  icon: 'check' | 'close';
  contexts: {
    name: string;
    usage: 'Allowed' | 'Disallowed';
    /**
     * Summary of the context's date range: when it becomes available, when it'll expire etc.
     */
    usageComment: string;
    /**
     * Prettified info about the start date of the range
     */
    start: string;
    /**
     * Prettified info about the end date of the range
     */
    end: string;
    icon: 'check' | 'close';
    color: 'green' | 'error';
  }[];
}
/**
 * This function calculates and derives all necessary data from usage rules, so that components
 * can easily display information without further calculation.
 */
export const getUsageRuleInfo = (usageRuleInfo: UsageRuleWithCalculatedValidity) => {
  const { contexts, hasDisallowedContext } = usageRuleInfo;
  let disallowedContexts = '';
  const contextInfo: UsageRuleInfo['contexts'] = contexts.map(ctx => {
    const isAllowed = ctx.hasReachedStartDate && !ctx.isExpired;
    if ((!isAllowed || ctx.warningLevel === 'always') && ctx.warningLevel !== 'never') disallowedContexts += `${disallowedContexts.length ? ', ' : ''}${ctx.name}`;
    return {
      name: ctx.name,
      usage: isAllowed ? 'Allowed' : 'Disallowed',
      usageComment: getUsageComment(ctx),
      start: getUsageRuleDateDetails(ctx.validity.from, ctx.validFrom, 'from'),
      end: getUsageRuleDateDetails(ctx.validity.to, ctx.validUntil, 'to'),
      color: isAllowed ? 'green' : 'error',
      icon: isAllowed ? 'check' : 'close',
    };
  });

  const info: UsageRuleInfo = {
    label: usageRuleInfo.identifier.label,
    summaryComment: disallowedContexts.length > 0 ? `This event has disallowed rule contexts: ${disallowedContexts}` : 'All rule contexts in this event are allowed',
    hasDisallowedContext,
    color: hasDisallowedContext ? 'error' : 'green',
    icon: hasDisallowedContext ? 'close' : 'check',
    contexts: contextInfo
  };
  return info;
};

const getUsageComment = (context: CalculatedUsageContext) => {
  const { validFrom, validUntil, hasReachedStartDate, isExpired } = context;
  if (!hasReachedStartDate && isExpired) {
    return 'Hasn\'t reached start date, but already expired!';
  }
  if (!hasReachedStartDate) {
    return `Becomes allowed on ${new Date(validFrom).toDateString()}`;
  }
  if (isExpired) {
    return `The rule has reached its expiry date (${new Date(validUntil).toDateString()})`;
  }
  if (validUntil !== 'forever') {
    return `Expires on ${new Date(validUntil).toDateString()}`;
  }

  return 'never expires';
};

const getUsageRuleDateDetails = (range: UsageRuleDateRange, dateString: string, type: 'from' | 'to') => {
  switch (range.offsetFrom) {
    case 'ingest_time':
      const ingestDate = new Date(dateString);
      const numberOfDays = range.untilNumberOfDays > 0 ? ` + ${range.untilNumberOfDays} day${range.untilNumberOfDays > 1 ? 's' : ''}` : '';
      const suffix = ` (Ingest time${numberOfDays})`;
      return `${ingestDate.toDateString()}${suffix}`;
    case 'absolute':
      return new Date(range.absoluteDate).toDateString();
    case 'forever':
      return (type === 'from' ? 'Since' : 'Until') + ' forever';
  }
};

export const getRestrictionComment = (restrictionItem: TimedMetadataItemWithUsageRule): string => {
  if (restrictionItem.usageRule) {
    const usageRuleWithValidity = getUsageRuleInfoFromTimedMdf(restrictionItem);
    if (usageRuleWithValidity) {
      const usageRuleInfo = getUsageRuleInfo(usageRuleWithValidity);
      return `<b>${htmlEscape(usageRuleInfo.label)}</b>: ${htmlEscape(usageRuleInfo.summaryComment)}`;
    }
  }
  return (restrictionItem.data.formData[RestrictedMaterialField.Comment] ?? 'Restricted material').toString();
};



/**
 * Returns true if item contains restricted material timed metadata in given range.
 * Will take into account associated usage rule (if any) according to current date.
 */
export function isActiveRestrictedMaterialItem(item: TimedMetadataItem, rangeToCheck: Segment | undefined): boolean {
  if (item?.data?.formId !== RESTRICTED_MATERIAL_MDF_ID) {
    return false;
  }

  const hasRestrictedMaterialInRange = !rangeToCheck || segmentOverlap(
    { start: item.startMs, end: item.endMs },
    rangeToCheck
  ) !== null;

  const usageRuleValidity = getUsageRuleInfoFromTimedMdf(item);
  const isRestrictedByUsageRule = usageRuleValidity ? isTimedMetadataRestrictedByUsageRule(usageRuleValidity) : true;

  return hasRestrictedMaterialInRange && isRestrictedByUsageRule;
}


function isTimedMetadataRestrictedByUsageRule(usageRuleValidity: UsageRuleWithCalculatedValidity) {
  return !!usageRuleValidity.contexts.find(
    ctx => ctx.warningLevel === 'always'
    || ((!ctx.hasReachedStartDate || ctx.isExpired) && ctx.warningLevel !== 'never')
  );
}


function hasRestrictedMaterial(metadata: { items: TimedMetadataItem[] }, rangeToCheck: Segment | undefined): boolean {
  return !!((metadata?.items ?? []).find(item => isActiveRestrictedMaterialItem(item, rangeToCheck)));
}


export const getRestrictedFormUsageRules = (mdfs: Pick<MetaDataSchema, 'id' | 'usageRules'>[]) => {
  const restrictedForm = mdfs.find(mdf => mdf.id === RESTRICTED_MATERIAL_MDF_ID);
  return restrictedForm && restrictedForm.usageRules;
};
